// Ronsoco help (sections/ronsoco-help.liquid): the FAQ page (search, topic grid / list, links to answers)
// and the "Need help?" chat. The chat isn't AI: it only shows the FAQ answers saved in the theme editor,
// and for orders it opens the carrier's tracking page with the shopper's tracking number. It never reads store data.
(() => {
  if (window.ronsocoHelp) return;

  // ---------- Answers (JSON from the section's blocks) ----------
  let entries = [];
  let index = [];

  // ---------- Search: plain keyword matching ----------
  const STOP = new Set(
    'a about am an and any are as at be been but by can cant could did do does doesnt dont for from get got had has have hello hey hi how i id if im in into is it its ive just me my no not of on or our please so some that the their them then there these they this to too us was we were what whats which who why will with would yes you your'.split(
      ' '
    )
  );

  // Words shoppers use for the same thing
  const GROUPS = [
    ['track', 'tracking', 'where', 'status', 'parcel', 'package', 'shipped', 'arrived'],
    ['delivery', 'deliver', 'shipping', 'ship', 'long', 'late', 'days', 'arrive', 'when'],
    ['return', 'refund', 'money', 'exchange', 'back'],
    ['damaged', 'damage', 'broken', 'faulty', 'defect', 'defective', 'torn', 'hole', 'wrong', 'missing', 'stain'],
    ['cancel', 'change', 'modify', 'edit'],
    ['size', 'sizing', 'fit', 'measure', 'measurements', 'small', 'large', 'big', 'tight', 'loose'],
    ['wash', 'washing', 'care', 'clean', 'dry', 'dryer', 'laundry', 'iron'],
    ['payment', 'pay', 'card', 'paypal', 'apple', 'google', 'visa', 'mastercard', 'amex'],
    ['currency', 'euro', 'pound', 'dollar', 'prices'],
    ['customs', 'duty', 'duties', 'tax', 'taxes', 'vat', 'import'],
    ['account', 'login', 'sign', 'password', 'guest'],
    ['pet', 'dog', 'cat', 'puppy', 'kitten', 'pouch'],
    ['cost', 'free', 'fee', 'much', 'charge'],
    ['country', 'countries', 'international', 'worldwide'],
  ];

  // Someone asking for a person: offer the contact page
  const CONTACT = ['contact', 'email', 'human', 'person', 'talk', 'speak', 'phone', 'call', 'agent', 'someone', 'staff'];

  const stem = (word) => {
    if (word.length > 5 && word.endsWith('ing')) return word.slice(0, -3);
    if (word.length > 4 && (word.endsWith('ies') || word.endsWith('ied'))) return `${word.slice(0, -3)}y`;
    if (word.length > 4 && word.endsWith('ed')) return word.slice(0, -2);
    if (word.length > 4 && word.endsWith('es')) return word.slice(0, -2);
    if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
    return word;
  };

  const words = (text) =>
    String(text || '')
      .toLowerCase()
      .replace(/<[^>]*>/g, ' ')
      .replace(/&[a-z0-9#]+;/g, ' ')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .filter(Boolean);

  const terms = (text) => words(text).filter((word) => !STOP.has(word)).map(stem);

  // "ship" finds "shipping", "size" finds "sizing"
  const same = (a, b) => a === b || (Math.min(a.length, b.length) >= 3 && (a.startsWith(b) || b.startsWith(a)));

  const STEMMED_GROUPS = GROUPS.map((group) => group.map(stem));
  const related = (term) => {
    const found = new Set();
    STEMMED_GROUPS.forEach((group) => {
      if (group.some((word) => same(word, term))) group.forEach((word) => found.add(word));
    });
    found.delete(term);
    return [...found];
  };

  const buildIndex = () => {
    const data = document.getElementById('RonsocoHelpData');
    try {
      entries = data ? JSON.parse(data.textContent) : [];
    } catch (e) {
      entries = [];
    }
    index = entries.map((entry) => ({
      entry,
      question: terms(entry.q),
      words: terms(entry.words),
      topic: terms(entry.topic),
      answer: terms(entry.a),
    }));
  };

  // Each word the shopper typed counts once, by its best match: in the question, in its extra search words,
  // in the topic or answer, then (a little) through a word that means the same thing
  const search = (text) => {
    const base = [...new Set(terms(text))].map((term) => ({ term, others: related(term) }));
    if (!base.length) return [];
    const inList = (list, term) => list.some((word) => same(word, term));
    return index
      .map((item) => {
        let score = 0;
        base.forEach(({ term, others }) => {
          if (inList(item.question, term)) score += 3;
          else if (inList(item.words, term)) score += 2.5;
          else if (inList(item.topic, term) || inList(item.answer, term)) score += 1;
          else if (others.some((word) => inList(item.question, word) || inList(item.words, word))) score += 1.2;
          else if (others.some((word) => inList(item.topic, word) || inList(item.answer, word))) score += 0.4;
        });
        return { entry: item.entry, score };
      })
      .filter((result) => result.score >= 2)
      .sort((a, b) => b.score - a.score);
  };

  // ---------- FAQ page ----------
  const pageRoot = () => document.querySelector('[data-rhp]');

  const openQuestion = (id) => {
    const question = document.getElementById(id);
    if (!question || question.tagName !== 'DETAILS') return;
    question.open = true;
    question.scrollIntoView({ block: 'start' });
    const summary = question.querySelector('summary');
    if (summary) summary.focus({ preventScroll: true });
  };

  const closeResults = () => {
    const page = pageRoot();
    if (!page) return;
    const results = page.querySelector('[data-rhp-results]');
    const input = page.querySelector('[data-rhp-search-input]');
    if (results) results.hidden = true;
    if (input) input.setAttribute('aria-expanded', 'false');
  };

  const showResults = (input) => {
    const page = pageRoot();
    const results = page.querySelector('[data-rhp-results]');
    const text = input.value.trim();
    results.replaceChildren();
    if (!text) {
      closeResults();
      return;
    }
    const found = search(text).slice(0, 6);
    if (!found.length) {
      const empty = document.createElement('li');
      empty.className = 'rhp__result-empty';
      empty.textContent = 'No matching questions. Try other words.';
      results.append(empty);
    }
    found.forEach(({ entry }) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      const link = document.createElement('a');
      link.className = 'rhp__result';
      link.href = `#q-${entry.id}`;
      link.dataset.rhpOpen = `q-${entry.id}`;
      const question = document.createElement('span');
      question.className = 'rhp__result-q';
      question.textContent = entry.q;
      link.append(question);
      if (entry.topic) {
        const topic = document.createElement('span');
        topic.className = 'rhp__result-topic';
        topic.textContent = entry.topic;
        link.append(topic);
      }
      item.append(link);
      results.append(item);
    });
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const setView = (view, remember) => {
    const page = pageRoot();
    if (!page) return;
    const topics = page.querySelector('[data-rhp-topics]');
    if (topics) topics.dataset.view = view;
    page.querySelectorAll('[data-rhp-view]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.rhpView === view);
    });
    if (remember) {
      try {
        localStorage.setItem('ronsoco-help-view', view);
      } catch (e) {}
    }
  };

  const initPage = () => {
    if (!pageRoot()) return;
    try {
      const saved = localStorage.getItem('ronsoco-help-view');
      if (saved === 'grid' || saved === 'list') setView(saved, false);
    } catch (e) {}
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id.startsWith('q-')) openQuestion(id);
  };

  // ---------- Chat ----------
  const chatRoot = () => document.querySelector('[data-rhc]');
  const touch = window.matchMedia('(hover: none)');
  const part = (name) => {
    const root = chatRoot();
    return root ? root.querySelector(`[data-rhc-${name}]`) : null;
  };
  const text = (name) => {
    const template = chatRoot().querySelector(`template[data-rhc-text="${name}"]`);
    return template ? template.content.textContent.trim() : '';
  };

  const scrollDown = () => {
    const scroller = part('scroll');
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  };

  const bubble = (who) => {
    const message = document.createElement('div');
    message.className = `rhc__msg rhc__msg--${who}`;
    part('log').append(message);
    return message;
  };

  const say = (message) => {
    bubble('user').textContent = message;
    part('quick').hidden = true;
    scrollDown();
  };

  // html: the store's own FAQ answer (from the theme editor); plain: a reply built here
  const reply = (content, plain) => {
    const message = bubble('bot');
    const body = document.createElement('div');
    body.className = 'rhc__msg-body';
    if (plain) {
      body.textContent = content;
    } else {
      body.innerHTML = content;
    }
    message.append(body);
    scrollDown();
    return message;
  };

  const actions = (message) => {
    const row = document.createElement('div');
    row.className = 'rhc__actions';
    message.append(row);
    return row;
  };

  const linkAction = (row, label, href, newTab) => {
    const link = document.createElement('a');
    link.className = 'rhc__action';
    link.href = href;
    link.textContent = label;
    if (newTab) {
      link.target = '_blank';
      link.rel = 'noopener';
    }
    row.append(link);
    return link;
  };

  const buttonAction = (row, label, data) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rhc__action';
    button.textContent = label;
    Object.assign(button.dataset, data);
    row.append(button);
    return button;
  };

  const contactAction = (message) => linkAction(actions(message), 'Contact us', chatRoot().dataset.contactUrl);

  let trackingCount = 0;
  const askTracking = (prompt) => {
    const message = reply(prompt || text('track'), true);
    trackingCount += 1;
    const id = `RonsocoChat-tracking-${trackingCount}`;
    const form = document.createElement('form');
    form.className = 'rhc__track';
    form.noValidate = true;
    form.dataset.rhcTrackForm = '';
    const label = document.createElement('label');
    label.className = 'visually-hidden';
    label.htmlFor = id;
    label.textContent = 'Tracking number';
    const input = document.createElement('input');
    input.id = id;
    input.className = 'rhc__track-input';
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.maxLength = 50;
    input.placeholder = 'Tracking number';
    input.setAttribute('autocapitalize', 'characters');
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'rhc__track-button';
    submit.textContent = 'Track';
    form.append(label, input, submit);
    message.append(form);
    buttonAction(actions(message), 'No tracking number yet?', { rhcNoTracking: '' });
    scrollDown();
    if (!touch.matches) input.focus({ preventScroll: true });
  };

  const cleanTracking = (value) => value.replace(/[\s-]+/g, '').toUpperCase();
  const looksLikeTracking = (value) => /^[A-Z0-9]{8,40}$/.test(value) && /\d/.test(value);

  const openTracking = (number) => {
    const pattern = chatRoot().dataset.trackUrl || 'https://t.17track.net/en#nums={number}';
    const url = pattern.replace('{number}', encodeURIComponent(number));
    window.open(url, '_blank', 'noopener');
    const message = reply(`Opening the tracking page for ${number} in a new tab. No update for 7 days? Contact us with your order number.`, true);
    const row = actions(message);
    linkAction(row, 'Open tracking page', url, true);
    linkAction(row, 'Contact us', chatRoot().dataset.contactUrl);
  };

  const noTracking = () => {
    const message = reply(text('no-tracking'), true);
    linkAction(actions(message), 'Sign in', chatRoot().dataset.accountUrl);
  };

  // An answer, then a couple more questions from the same topic
  const answer = (entry, suggestions) => {
    const message = reply(entry.a || '');
    if (entry.track) {
      askTracking();
      return;
    }
    const more = suggestions || entries.filter((other) => other.id !== entry.id && other.topic && other.topic === entry.topic).slice(0, 2);
    if (more.length) {
      const row = actions(message);
      more.forEach((other) => buttonAction(row, other.label || other.q, { rhcAsk: other.id }));
    }
  };

  const handleQuestion = (value) => {
    const message = value.trim();
    if (!message) return;
    say(message);

    // A tracking number on its own
    const compact = cleanTracking(message);
    if (!/\s/.test(message) && looksLikeTracking(compact)) {
      openTracking(compact);
      return;
    }

    // An order number ("#1001", "order 1001"): the chat can't look orders up
    if (/#\s?\d{3,}/.test(message) || /\border\s*(number|no\.?)?\s*#?\s*\d{3,}/i.test(message)) {
      askTracking(text('order-number'));
      return;
    }

    const found = search(message);
    const asksPerson = terms(message).some((term) => CONTACT.some((word) => same(word, term)));
    if (asksPerson && (!found.length || found[0].score < 4)) {
      contactAction(reply(text('contact'), true));
      return;
    }
    if (!found.length) {
      contactAction(reply(text('no-match'), true));
      return;
    }
    const best = found[0];
    const close = found.slice(1, 3).filter((result) => result.score >= best.score * 0.6).map((result) => result.entry);
    answer(best.entry, close);
  };

  const cookieBannerOpen = () => {
    const banner = document.getElementById('shopify-pc__banner');
    // position: fixed, so offsetParent is always null: check its boxes instead
    if (!banner || !banner.getClientRects().length) return false;
    const style = getComputedStyle(banner);
    return style.visibility !== 'hidden' && style.opacity !== '0';
  };

  // The button waits for the cookie banner (it sits at the bottom of the screen too)
  const refreshLauncher = () => {
    const launcher = part('launcher');
    if (launcher) launcher.hidden = cookieBannerOpen();
  };

  const openChat = () => {
    const root = chatRoot();
    const panel = part('panel');
    if (!root || !panel) return false;
    if (!panel.open) {
      panel.show();
      root.classList.add('is-open');
      part('launcher').setAttribute('aria-expanded', 'true');
      refreshCartCount();
    }
    return true;
  };

  // The cart icon's number, fresh each time the chat opens (the cart may have changed since the page loaded)
  const refreshCartCount = () => {
    const count = chatRoot()?.querySelector('[data-rhc-cart-count]');
    if (!count) return;
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) return;
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
        const link = count.closest('[data-rhc-cart]');
        link?.setAttribute('aria-label', `${link.dataset.label} (${cart.item_count})`);
      })
      .catch(() => {});
  };

  const openNotice = () => {
    const notice = part('notice');
    if (!notice) return;
    notice.hidden = false;
    notice.querySelector('.rhc__notice-ok')?.focus({ preventScroll: true });
  };

  const closeNotice = () => {
    const notice = part('notice');
    if (!notice || notice.hidden) return false;
    notice.hidden = true;
    chatRoot()?.querySelector('[data-rhc-notice-open]')?.focus({ preventScroll: true });
    return true;
  };

  const closeChat = (returnFocus) => {
    const root = chatRoot();
    const panel = part('panel');
    if (!panel || !panel.open) return;
    panel.close();
    root.classList.remove('is-open');
    const launcher = part('launcher');
    launcher.setAttribute('aria-expanded', 'false');
    if (returnFocus) launcher.focus({ preventScroll: true });
  };

  // ---------- Events (on the document, so they survive theme-editor reloads) ----------
  document.addEventListener('click', (event) => {
    // FAQ page: popular cards and search results open their answer
    const opener = event.target.closest('[data-rhp-open]');
    if (opener) {
      event.preventDefault();
      window.history.replaceState(null, '', `#${opener.dataset.rhpOpen}`);
      closeResults();
      openQuestion(opener.dataset.rhpOpen);
      return;
    }

    const view = event.target.closest('[data-rhp-view]');
    if (view) {
      setView(view.dataset.rhpView, true);
      return;
    }

    if (!event.target.closest('[data-rhp-search]')) closeResults();

    if (!chatRoot()) return;

    // "Track Your Order" links (footer menu item /account#track-order, FAQ page button)
    const trackLink = event.target.closest('a[href$="#track-order"]');
    if (trackLink) {
      event.preventDefault();
      openChat();
      askTracking();
      return;
    }

    if (event.target.closest('[data-rhc-launcher]')) {
      openChat();
      // Phones: focus moves into the panel without opening the keyboard
      part(touch.matches ? 'close' : 'input').focus({ preventScroll: true });
      return;
    }

    if (event.target.closest('[data-rhc-close]')) {
      closeNotice();
      closeChat(true);
      return;
    }

    if (event.target.closest('[data-rhc-notice-open]')) {
      openNotice();
      return;
    }

    if (event.target.closest('[data-rhc-notice-close]') || event.target.matches('[data-rhc-notice]')) {
      closeNotice();
      return;
    }

    if (event.target.closest('[data-rhc-track]')) {
      say(event.target.closest('[data-rhc-track]').textContent.trim());
      askTracking();
      return;
    }

    if (event.target.closest('[data-rhc-no-tracking]')) {
      noTracking();
      return;
    }

    const ask = event.target.closest('[data-rhc-ask]');
    if (ask) {
      const entry = entries.find((item) => item.id === ask.dataset.rhcAsk);
      if (!entry) return;
      say(ask.textContent.trim());
      answer(entry);
    }
  });

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-rhc-form]')) {
      event.preventDefault();
      const input = part('input');
      handleQuestion(input.value);
      input.value = '';
      return;
    }

    if (event.target.matches('[data-rhc-track-form]')) {
      event.preventDefault();
      const input = event.target.querySelector('input');
      const number = cleanTracking(input.value);
      const error = event.target.parentElement.querySelector('.rhc__track-error');
      if (!looksLikeTracking(number)) {
        if (!error) {
          const note = document.createElement('p');
          note.className = 'rhc__track-error';
          note.setAttribute('role', 'alert');
          note.textContent = "That doesn't look like a tracking number. You'll find it in your shipping email.";
          event.target.after(note);
        }
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      if (error) error.remove();
      input.removeAttribute('aria-invalid');
      say(number);
      openTracking(number);
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-rhp-search-input]')) showResults(event.target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.target.matches && event.target.matches('[data-rhp-search-input]')) {
      const page = pageRoot();
      const links = [...page.querySelectorAll('[data-rhp-results] a')];
      if (event.key === 'Enter' && links.length) {
        event.preventDefault();
        links[0].click();
      } else if (event.key === 'ArrowDown' && links.length) {
        event.preventDefault();
        links[0].focus();
      } else if (event.key === 'Escape') {
        event.target.value = '';
        closeResults();
      }
      return;
    }

    if (event.target.closest && event.target.closest('[data-rhp-results]')) {
      const links = [...pageRoot().querySelectorAll('[data-rhp-results] a')];
      const at = links.indexOf(event.target);
      if (event.key === 'ArrowDown' && at < links.length - 1) {
        event.preventDefault();
        links[at + 1].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (at > 0) links[at - 1].focus();
        else pageRoot().querySelector('[data-rhp-search-input]').focus();
      } else if (event.key === 'Escape') {
        closeResults();
        pageRoot().querySelector('[data-rhp-search-input]').focus();
      }
      return;
    }

    if (event.key === 'Escape' && event.target.closest && event.target.closest('[data-rhc-panel]')) {
      if (!closeNotice()) closeChat(true);
    }
  });

  window.addEventListener('hashchange', () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id.startsWith('q-')) openQuestion(id);
  });

  const init = () => {
    buildIndex();
    initPage();
    refreshLauncher();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The cookie banner comes and goes after the page loads
  setInterval(refreshLauncher, 1000);

  // Theme editor: new answers after edits; open the chat while the section is selected
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-rhc], [data-rhp]')) init();
  });
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('[data-rhc]')) openChat();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.querySelector('[data-rhc]')) closeChat(false);
  });

  window.ronsocoHelp = { search, openChat, closeChat, askTracking, openQuestion };
})();
